// ABOUTME: Adds an item to the active cart with snackbar feedback: an "adding…" toast replaced by success/error.
// ABOUTME: Ignores a repeat add of the same item while the first one is still in flight.

import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { AddToCartOptions } from '../../hooks/useCart';
import { useCartContext } from './CartContext';

// Items whose add is in flight, keyed by product and configuration. Shared by every caller of the
// hook so the same item added from different places is still sent once.
const pendingAdds = new Set<string>();

/**
 * Returns `addToCart`, resolving to true once the item is in the cart and false when it failed,
 * was skipped as a duplicate, or there is no cart. It never throws: failures are shown as a snackbar.
 */
export function useAddToCart() {
    const { formatMessage } = useIntl();
    const cart = useCartContext();

    const addToCart = useCallback(
        async (productId: string, quantity = 1, options?: AddToCartOptions) => {
            if (!cart) return false;
            const pendingKey = JSON.stringify([productId, options?.configurationJson ?? null]);
            if (pendingAdds.has(pendingKey)) return false;
            pendingAdds.add(pendingKey);

            // Persistent until replaced; duplicates allowed so parallel adds each get their own toast.
            const progressKey = SnackBarUtils.toast({
                msg: formatMessage({
                    id: 'cpq.cart.adding',
                    defaultMessage: 'Adding product to cart…',
                }),
                variant: 'info',
                persist: true,
                preventDuplicate: false,
            });
            const replaceKey = progressKey || undefined;
            try {
                await cart.addToCart(productId, quantity, options);
                SnackBarUtils.success(
                    formatMessage({
                        id: 'cpq.cart.catalog.added',
                        defaultMessage: 'Product added to cart',
                    }),
                    { replaceKey },
                );
                return true;
            } catch (err) {
                SnackBarUtils.error(
                    err instanceof Error && err.message
                        ? err.message
                        : formatMessage({
                              id: 'cpq.cart.add-error',
                              defaultMessage: 'Failed to add product to cart',
                          }),
                    { replaceKey },
                );
                return false;
            } finally {
                pendingAdds.delete(pendingKey);
            }
        },
        [cart, formatMessage],
    );

    return { addToCart };
}
